import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { setCompanyTz } from "@/lib/tz";

/**
 * The company's timezone as React state, so a screen re-renders the moment it
 * is known. A localStorage-only cache left the first render after login
 * showing the viewer's browser zone until something else happened to
 * re-render — a New York crew's shift appearing on Tokyo time.
 */
const TzContext = createContext({ tz: null, ready: false, refresh: () => {} });

export function CompanyTzProvider({ children }) {
  const [tz, setTz] = useState(null);
  const [ready, setReady] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/company");
      const zone = data?.timezone || "UTC";
      setCompanyTz(zone);   // lib/tz.js reads this for non-React callers
      setTz(zone);
    } catch {
      setTz(null);          // display falls back to the browser zone
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Hooks must run on every render, so the value is built before the gate.
  const value = useMemo(() => ({ tz, ready, refresh }), [tz, ready, refresh]);

  // Hold the first paint until the zone is known. Rendering a timestamp table
  // against the browser zone for a few hundred milliseconds puts a New York
  // crew's shift on Tokyo time, which is worse than a brief placeholder.
  if (!ready) {
    return (
      <div className="p-10 font-mono text-sm" data-testid="tz-loading">LOADING…</div>
    );
  }

  return <TzContext.Provider value={value}>{children}</TzContext.Provider>;
}

export const useCompanyTz = () => useContext(TzContext);
